import AdjustIcon from '@mui/icons-material/Adjust';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Button, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { FC, useMemo, useState } from 'react';

import { CameraInfo } from '@apiContract';
import { MotionContainer, varBounce } from '@components/Animate';
import { AutomatedCameraLocalStorageKeys, CapturePiece, MapCapturePiece } from '@components/Capture/types';
import useLocalStorage from '@hooks/useLocalStorage';
import { m } from 'framer-motion';
import orderBy from 'lodash/fp/orderBy';
import { useRDInstance } from 'src/hooks/useRdInstance';

import Divider from '@components/Divider';

import CameraStatus from './CameraStatus';
import { Delete } from './Delete';
import FilesList from './FilesList';
import { sxContainer, sxHeaderItem, sxImageContainer, sxMain, sxSortRow } from './styles';

type Props = {
  files: MapCapturePiece;
  onStartAutomatedCapture: (cameraIndex: number) => void;
  onStopAutomatedCapture: VoidFunction;
  sessionId: string;
  onDelete: VoidFunction;
  isCaptureInProgress: boolean;
  onChooseCamera: VoidFunction;
  userCamera: CameraInfo;
};

enum SortOrder {
  DESC = 'desc',
  ASC = 'asc',
}

const AutomatedCapture: FC<Props> = ({
  files,
  onStartAutomatedCapture,
  onStopAutomatedCapture,
  sessionId,
  onDelete,
  isCaptureInProgress,
  onChooseCamera,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortOrder, setSortOrder] = useLocalStorage<SortOrder>(
    AutomatedCameraLocalStorageKeys.DraftPiecesSortOrder,
    SortOrder.ASC,
  );

  const { getCameraData } = useRDInstance();

  const cameraData = getCameraData();

  const filesQuantity = Object.keys(files).length;

  const startCapture = () => {
    if (cameraData.defaultCamera?.index !== undefined) {
      onStartAutomatedCapture(cameraData.defaultCamera?.index)
    }
  }

  const handleCapture = isCaptureInProgress ? onStopAutomatedCapture : startCapture;
  const onDeleteFinish = () => {
    onDelete();
    setShowDeleteModal(false);
  };

  const sortedFiles = useMemo(() => {
    const fileValues = Object.values(files);
    const sorter = orderBy([(file: CapturePiece) => file?.piece?.pieceLocation?.orderNo])([sortOrder]);

    return sorter(fileValues);
  }, [files, sortOrder]);

  return (
    <Box sx={sxMain}>
      <Box sx={sxContainer}>
        <Box sx={sxHeaderItem}>
          <CameraStatus onChooseCamera={onChooseCamera} />
        </Box>

        <Divider orientation="vertical" dashed />

        <Box sx={sxHeaderItem}>
          <Button
            size="large"
            variant="soft"
            color={isCaptureInProgress ? 'error' : 'inherit'}
            startIcon={isCaptureInProgress ? <StopIcon /> : <AdjustIcon />}
            onClick={handleCapture}
            disabled={cameraData.defaultCamera?.index === undefined}
          >
            {isCaptureInProgress ? 'Stop Capturing' : 'Start Capturing'}
          </Button>
        </Box>

        <Divider orientation="vertical" dashed />

        <Box sx={sxHeaderItem}>
          <Typography variant="h4">{filesQuantity}</Typography>
          <Typography variant="body2" color="text.secondary">
            images captured
          </Typography>
        </Box>
      </Box>

      <Box sx={sxSortRow}>
        <Box>
          <Typography variant="body2" fontWeight="bold" display="inline">
            Sort by:
          </Typography>
          <Select
            variant="standard"
            value={sortOrder}
            sx={{ ml: 1, width: 80 }}
            onChange={({ target }: SelectChangeEvent<SortOrder>) => {
              setSortOrder(target.value as SortOrder);
            }}
          >
            <MenuItem value={SortOrder.DESC}>Newest</MenuItem>
            <MenuItem value={SortOrder.ASC}>Oldest</MenuItem>
          </Select>
        </Box>

        <Button
          size="small"
          variant="text"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          disabled={filesQuantity === 0 || isCaptureInProgress}
          onClick={() => setShowDeleteModal(true)}
        >
          Delete All
        </Button>
      </Box>

      {filesQuantity > 0 ? (
        <FilesList items={sortedFiles} />
      ) : (
        <Box sx={sxImageContainer}>
          <MotionContainer>
            <m.div variants={varBounce('in')}>
              <Box sx={{ my: 10, textAlign: 'center' }}>
                <img src="/assets/illustrations/folder-empty.svg" alt="No Images" />
                <Typography sx={{ mt: 1 }} variant="h6" color="text.disabled">
                  No images to display
                </Typography>
              </Box>
            </m.div>
          </MotionContainer>
        </Box>
      )}
      <Delete
        sessionId={sessionId}
        actionStarted={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onActionFinish={onDeleteFinish}
      />
    </Box>
  );
};

export default AutomatedCapture;
